import { Calendar, ChevronDown, MessageSquare, Smartphone, Star, User } from 'lucide-react'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { appStoreReviews } from 'sprites-types/workflow/tools/appStoreReviews'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { ContextMenuWrapper } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function ReviewItem({ review, className }: { review: any; className?: string }) {
  const author = review.author || review.reviewer || review.user || 'Anonymous'
  const rating = review.rating || review.score || review.stars || 0
  const title = review.title || review.subject || review.headline || ''
  const content = review.content || review.text || review.body || review.review || ''
  const date = review.date || review.createdAt || review.timestamp || ''
  const version = review.version || review.appVersion || ''

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString()
    } catch {
      return dateStr
    }
  }

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-3 w-3 ${i < rating ? 'fill-yellow-500 text-yellow-500' : 'text-gray-300'}`}
      />
    ))
  }

  return (
    <div
      className={`rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-sm ${className}`}
    >
      <div className="px-4 py-3">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex-shrink-0">
            <User className="h-4 w-4 text-blue-600" />
          </div>

          <div className="min-w-0 flex-1">
            <div className="mb-1 flex items-center gap-2">
              <div className="text-sm font-medium text-gray-900">{author}</div>
              <div className="flex items-center gap-1">{renderStars(rating)}</div>
            </div>

            <div className="mb-2 flex items-center gap-3 text-xs text-gray-600">
              {date && (
                <div className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  <span>{formatDate(date)}</span>
                </div>
              )}
              {version && <div>v{version}</div>}
            </div>

            {title && <div className="mb-2 text-sm font-medium text-gray-800">{title}</div>}

            {content && (
              <div className="text-xs leading-relaxed text-gray-700">
                {content.length > 200 ? `${content.slice(0, 200)}...` : content}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function AppStoreReviewsDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(appStoreReviews, nodeExecution.inputs)
  const [isExpanded, setIsExpanded] = useState(true)

  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  const appId = inputs.appId || inputs.id || inputs.bundleId || ''
  const country = inputs.country || inputs.region || 'US'
  const reviews = Array.isArray(result)
    ? result
    : result &&
        typeof result === 'object' &&
        'data' in result &&
        Array.isArray((result as any).data)
      ? (result as any).data
      : result &&
          typeof result === 'object' &&
          'reviews' in result &&
          Array.isArray((result as any).reviews)
        ? (result as any).reviews
        : result &&
            typeof result === 'object' &&
            'results' in result &&
            Array.isArray((result as any).results)
          ? (result as any).results
          : result
            ? [result]
            : []
  const averageRating =
    reviews.length > 0
      ? reviews.reduce((sum: number, r: any) => sum + (r.rating || r.score || r.stars || 0), 0) /
        reviews.length
      : 0

  if (reviews.length === 0) {
    return (
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex-shrink-0">
            <MessageSquare className="h-5 w-5 text-gray-400" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-gray-900">No reviews found</div>
            {appId && <div className="text-xs text-gray-600">App ID: {appId}</div>}
          </div>
        </div>
      </div>
    )
  }

  if (reviews.length === 1) {
    return <ReviewItem review={reviews[0]} />
  }

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-3 w-3 ${i < rating ? 'fill-yellow-500 text-yellow-500' : 'text-gray-300'}`}
      />
    ))
  }

  return (
    <div className="space-y-3">
      <ContextMenuWrapper actions={[]}>
        <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-gray-200/70 text-black shadow-sm">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group flex w-full items-center gap-4 bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
            aria-expanded={isExpanded}
          >
            <div className="flex-shrink-0">
              <Smartphone className="h-5 w-5 text-blue-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-gray-900">App Store Reviews</div>
              <div className="flex items-center gap-2 text-xs font-medium tracking-wide text-gray-500">
                {reviews.length} reviews • {country}
                {averageRating > 0 && (
                  <div className="flex items-center gap-1">
                    {renderStars(Math.round(averageRating))}
                    <span>{averageRating.toFixed(1)}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex-shrink-0">
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </div>
          </button>
        </div>
      </ContextMenuWrapper>

      {isExpanded && (
        <div className="space-y-2">
          {reviews.slice(0, 10).map((review: any, index: number) => (
            <ReviewItem key={index} review={review} className="mx-8" />
          ))}
          {reviews.length > 10 && (
            <div className="mx-8 py-2 text-center text-xs text-gray-500">
              Showing first 10 of {reviews.length} reviews
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function downloadAppStoreReviews(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function appStoreReviewsToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(appStoreReviews, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  const appId = inputs.appId || inputs.id || inputs.bundleId || ''
  const country = inputs.country || inputs.region || 'US'
  const reviews = Array.isArray(result)
    ? result
    : (result as any)?.data ||
      (result as any)?.reviews ||
      (result as any)?.results ||
      (result ? [result] : [])

  if (reviews.length === 0) {
    return `## App Store Reviews

${appId ? `**App ID:** ${appId}` : ''}
**Country:** ${country}

No reviews found.

---
`
  }

  const averageRating =
    reviews.reduce((sum: number, r: any) => sum + (r.rating || r.score || r.stars || 0), 0) /
    reviews.length

  const tableHeader = `| Author | Rating | Title | Date | Review |
|--------|--------|-------|------|--------|`

  const tableRows = reviews
    .slice(0, 20) // Limit to prevent huge markdown files
    .map((review: any) => {
      const author = review.author || review.reviewer || review.user || 'Anonymous'
      const rating = `${'⭐'.repeat(review.rating || review.score || review.stars || 0)} (${review.rating || review.score || review.stars || 0}/5)`
      const title = review.title || review.subject || review.headline || '-'
      const date =
        review.date || review.createdAt || review.timestamp
          ? new Date(review.date || review.createdAt || review.timestamp).toLocaleDateString()
          : '-'
      const content = (
        review.content ||
        review.text ||
        review.body ||
        review.review ||
        'No content'
      )
        .replace(/\|/g, '\\|') // Escape pipe characters
        .replace(/\n/g, ' ') // Replace newlines with spaces
        .slice(0, 100) // Limit length for table readability
      const truncatedContent = content.length === 100 ? content + '...' : content

      return `| ${author} | ${rating} | ${title} | ${date} | ${truncatedContent} |`
    })
    .join('\n')

  const detailedReviews = reviews
    .slice(0, 10) // Show fewer detailed reviews
    .filter(
      (review: any) => (review.content || review.text || review.body || review.review)?.length > 100
    )
    .map((review: any) => {
      const author = review.author || review.reviewer || review.user || 'Anonymous'
      const content = review.content || review.text || review.body || review.review || ''
      return `### ${author}\n\n${content}`
    })
    .join('\n\n')

  return `## App Store Reviews (${reviews.length} reviews)

${appId ? `**App ID:** ${appId}` : ''}
**Country:** ${country}
**Average Rating:** ${averageRating.toFixed(1)}/5 ⭐

${tableHeader}
${tableRows}

${detailedReviews ? `\n## Detailed Reviews\n\n${detailedReviews}\n` : ''}
${reviews.length > 20 ? `\n*Showing first 20 of ${reviews.length} reviews*` : ''}

---
`
}

export const AppStoreReviewsDisplay = createWorkflowToolComponent(
  AppStoreReviewsDisplayComponent,
  downloadAppStoreReviews,
  appStoreReviewsToMarkdown
)
